import { useImportDataOptions } from "@/hooks/use-import-data.hook.ts";
import { useImportDataEditContext } from "@/pages/settings/import-data/edit/import-data-edit.types.ts";
import { ApiImportDataType, ApiImportGlobalUpdateBehavior } from "@/types/import-data.types.ts";
import { Button, Radio, Search, Stack, Title } from "@skailer-com/uikit";
import { ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const behaviorKeys: Record<ApiImportGlobalUpdateBehavior, string> = {
  [ApiImportGlobalUpdateBehavior.create_only]: "create",
  [ApiImportGlobalUpdateBehavior.update_only]: "update",
  [ApiImportGlobalUpdateBehavior.upsert]: "upsert",
};

const timeOffTypes: ApiImportDataType[] = [
  ApiImportDataType.time_off_balances,
  ApiImportDataType.time_off_history,
];

const standardBehaviors: ApiImportGlobalUpdateBehavior[] = [
  ApiImportGlobalUpdateBehavior.create_only,
  ApiImportGlobalUpdateBehavior.update_only,
];

function PresetSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack>
      <Title size={"xs"} header={title} paddingTop paddingBottom />
      <Stack className={"border border-dark/5 rounded-sm py-2 px-4 bg-dark/5"}>{children}</Stack>
    </Stack>
  );
}

export function ImportDataPresetChoice() {
  const { t } = useTranslation();
  const { getImportDataTypeTitle } = useImportDataOptions();
  const [searchQuery, setSearchQuery] = useState("");
  const {
    importType,
    importBehavior,
    setImportType,
    setImportBehavior,
    setImportId,
    setImportPayload,
    setSelectedCustomTableId,
    setSelectedCustomTableName,
    nextButtonText,
    goToNextStep,
  } = useImportDataEditContext();

  const getPresetInfo = (type: ApiImportDataType, behavior: ApiImportGlobalUpdateBehavior) => ({
    title: t(`import_data:preset_${type}_${behaviorKeys[behavior]}_title`),
    caption: t(`import_data:preset_${type}_${behaviorKeys[behavior]}_caption`),
  });

  const handlePresetChange = (type: ApiImportDataType, behavior: ApiImportGlobalUpdateBehavior) => {
    setImportType(type);
    setImportBehavior(behavior);
    setImportId(undefined);
    setImportPayload(null);
    setSelectedCustomTableId(null);
    setSelectedCustomTableName(null);
  };

  const matchesQuery = (query: string, ...texts: string[]) =>
    texts.some((text) => text.toLowerCase().includes(query));

  const { filteredTypes, showTimeOffSection } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const standardTypes = Object.values(ApiImportDataType).filter(
      (type) => !timeOffTypes.includes(type)
    );

    if (!query) {
      return { filteredTypes: standardTypes, showTimeOffSection: true };
    }

    const filteredTypes = standardTypes.filter((type) => {
      const createInfo = getPresetInfo(type, ApiImportGlobalUpdateBehavior.create_only);
      const updateInfo = getPresetInfo(type, ApiImportGlobalUpdateBehavior.update_only);
      return matchesQuery(
        query,
        getImportDataTypeTitle(type),
        createInfo.title,
        createInfo.caption,
        updateInfo.title,
        updateInfo.caption
      );
    });

    const showTimeOffSection =
      matchesQuery(query, t("time_off:settings_title")) ||
      timeOffTypes.some((type) => {
        const info = getPresetInfo(type, ApiImportGlobalUpdateBehavior.upsert);
        return matchesQuery(query, info.title, info.caption);
      });

    return { filteredTypes, showTimeOffSection };
  }, [searchQuery]);

  const renderPresetRadio = (type: ApiImportDataType, behavior: ApiImportGlobalUpdateBehavior) => {
    const info = getPresetInfo(type, behavior);
    return (
      <Radio
        key={`${type}_${behavior}`}
        value={type}
        checked={importType === type && importBehavior === behavior}
        onChange={() => handlePresetChange(type, behavior)}
        title={info.title}
        caption={info.caption}
      />
    );
  };

  return (
    <Stack gap={"sm"}>
      <Stack className={"card-panel"} gap={"2xl"}>
        <Title
          size={"lg"}
          header={t("import_data:preset_choice_title")}
          caption={t("import_data:preset_choice_caption")}
        />

        <Stack>
          <Search
            placeholder={t("common:search")}
            value={searchQuery}
            onInput={(e) => setSearchQuery(e.currentTarget.value)}
          />

          {filteredTypes.map((type) => (
            <PresetSection key={type} title={getImportDataTypeTitle(type)}>
              {standardBehaviors.map((behavior) => renderPresetRadio(type, behavior))}
            </PresetSection>
          ))}

          {showTimeOffSection && (
            <PresetSection title={t("approval:time_off_approvals")}>
              {timeOffTypes.map((type) =>
                renderPresetRadio(type, ApiImportGlobalUpdateBehavior.upsert)
              )}
            </PresetSection>
          )}
        </Stack>
      </Stack>

      <Button size={"lg"} onClick={goToNextStep}>
        {nextButtonText}
      </Button>
    </Stack>
  );
}
